import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import { Textarea } from "../components/ui/textarea"
import { toast } from 'sonner'
import 'leaflet/dist/leaflet.css'
import '../styles/map.css'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

const defaultCenter = [51.1657, 10.4515]

function MapUpdater({ coordinates }) {
  const map = useMap()
  
  useEffect(() => {
    if (coordinates) {
      map.setView(coordinates, 16, {
        animate: true,
        duration: 1
      })
    }
  }, [coordinates, map])

  return null
}

export default function LeadDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [lead, setLead] = useState(null)
  const [localLead, setLocalLead] = useState(null)
  const [coordinates, setCoordinates] = useState(null)
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])

  useEffect(() => {
    fetchLead()
    if (user.role === 'admin') {
      fetchUsers()
    }
  }, [id, user.role])

  useEffect(() => {
    if (lead) {
      setLocalLead(lead)
    }
  }, [lead])

  async function geocodeAddress(address) {
    const query = `${address.street}, ${address.postal_code} ${address.city}, ${address.country}`
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
    )
    const data = await response.json()
    
    if (data && data[0]) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      }
    }
    return null
  }

  async function fetchLead() {
    try {
      const data = await api.getLead(id)
      
      data.contact_info = data.contact_info || { email: '', phone: '' }
      data.address = data.address || { 
        street: '', 
        city: '', 
        postal_code: '',
        country: 'Deutschland'
      }

      setLead(data)
      
      if (data.address?.lat && data.address?.lng) {
        setCoordinates([data.address.lat, data.address.lng])
      } else if (data.address) {
        const coords = await geocodeAddress(data.address)
        if (coords) {
          setCoordinates([coords.lat, coords.lng])
          await updateLead('address', {
            ...data.address,
            lat: coords.lat,
            lng: coords.lng
          })
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden des Leads:', error)
      toast.error('Lead konnte nicht geladen werden')
    }
  }

  async function updateLead(field, value) {
    setLoading(true);
    try {
      let updateData = {};

      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updateData = {
          [parent]: {
            ...localLead[parent],
            [child]: value
          }
        };
      } else {
        updateData = { [field]: value };
      }

      if (field === 'address' || field.startsWith('address.')) {
        const newAddress = field === 'address' ? value : {
          ...localLead.address,
          [field.split('.')[1]]: value
        };
        const coords = await geocodeAddress(newAddress);
        if (coords) {
          if (field === 'address') {
            updateData.address = {
              ...value,
              lat: coords.lat,
              lng: coords.lng
            };
          } else {
            updateData.address = {
              ...localLead.address,
              [field.split('.')[1]]: value,
              lat: coords.lat,
              lng: coords.lng
            };
          }
          setCoordinates([coords.lat, coords.lng]);
        }
      }

      // Sende nur das aktualisierte Feld an die API
      await api.updateLead(id, updateData);
      
      // Aktualisiere den lokalen State
      setLead(prev => ({
        ...prev,
        ...updateData
      }));
      
      toast.success('Änderungen gespeichert');
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Leads:', error);
      toast.error('Änderungen konnten nicht gespeichert werden');
    } finally {
      setLoading(false);
    }
  }

  async function updateLeadStatus(status) {
    setLoading(true);
    try {
      await api.updateLeadStatus(id, status);
      setLead(prev => ({
        ...prev,
        status
      }));
      toast.success('Status wurde aktualisiert');
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Lead-Status:', error);
      toast.error('Status konnte nicht aktualisiert werden');
    } finally {
      setLoading(false);
    }
  }

  function handleLocalChange(field, value) {
    setLocalLead(prev => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.')
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        }
      }
      return {
        ...prev,
        [field]: value
      }
    })
  }

  async function fetchUsers() {
    try {
      const data = await api.getUsers()
      setUsers(data.filter(u => u.status === 'active'))
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error)
      toast.error('Benutzer konnten nicht geladen werden')
    }
  }

  if (!localLead) return <div>Lädt...</div>

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Lead Details</h1>
        <Button onClick={() => navigate('/leads')}>
          Zurück zur Übersicht
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Kundendaten</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Input
                  value={localLead.customer_name}
                  onChange={(e) => handleLocalChange('customer_name', e.target.value)}
                  onBlur={() => updateLead('customer_name', localLead.customer_name)}
                  placeholder="Kundenname"
                />
              </div>
              <div>
                <Input
                  value={localLead.contact_info.email}
                  onChange={(e) => handleLocalChange('contact_info.email', e.target.value)}
                  onBlur={() => updateLead('contact_info', localLead.contact_info)}
                  placeholder="E-Mail"
                  type="email"
                />
              </div>
              <div>
                <Input
                  value={localLead.contact_info.phone}
                  onChange={(e) => handleLocalChange('contact_info.phone', e.target.value)}
                  onBlur={() => updateLead('contact_info', localLead.contact_info)}
                  placeholder="Telefon"
                />
              </div>
              <div>
                <Textarea
                  value={localLead.notes || ''}
                  onChange={(e) => handleLocalChange('notes', e.target.value)}
                  onBlur={() => updateLead('notes', localLead.notes)}
                  placeholder="Notizen"
                />
              </div>
            </CardContent>
          </Card>

          {user.role === 'admin' && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Lead-Status</CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={localLead.status}
                  onValueChange={(value) => updateLeadStatus(value)}
                  disabled={loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Status auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Neu</SelectItem>
                    <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                    <SelectItem value="approved">Freigegeben</SelectItem>
                    <SelectItem value="completed">Abschluss</SelectItem>
                    <SelectItem value="rejected">Abgelehnt</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Adresse</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Input
                  value={localLead.address.street}
                  onChange={(e) => handleLocalChange('address.street', e.target.value)}
                  onBlur={() => updateLead('address', localLead.address)}
                  placeholder="Straße"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  value={localLead.address.postal_code}
                  onChange={(e) => handleLocalChange('address.postal_code', e.target.value)}
                  onBlur={() => updateLead('address', localLead.address)}
                  placeholder="PLZ"
                />
                <Input
                  value={localLead.address.city}
                  onChange={(e) => handleLocalChange('address.city', e.target.value)}
                  onBlur={() => updateLead('address', localLead.address)}
                  placeholder="Stadt"
                />
              </div>
              <div>
                <Input
                  value={localLead.address.country}
                  onChange={(e) => handleLocalChange('address.country', e.target.value)}
                  onBlur={() => updateLead('address', localLead.address)}
                  placeholder="Land"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Karte</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px] rounded-lg overflow-hidden">
                <MapContainer
                  center={coordinates || defaultCenter}
                  zoom={coordinates ? 16 : 6}
                  className="h-full"
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {coordinates && (
                    <Marker position={coordinates}>
                      <Popup>
                        {localLead.customer_name}<br />
                        {localLead.address.street}<br />
                        {localLead.address.postal_code} {localLead.address.city}
                      </Popup>
                    </Marker>
                  )}
                  <MapUpdater coordinates={coordinates} />
                </MapContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
} 